/**
 * @file Account.tsx
 * @description Account page component with tabbed interface for account info and settings.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useUserDetails } from "../../context/UserDetailsContext";
import UserAvatar from "../../components/common/UserAvatar";
import AlphabetBackground from "../../components/common/AlphabetBackground";
import CrownIcon from "../../components/common/icons/CrownIcon";
import ClientList from "../../components/settings/ClientList";
import WebsiteList from "../../components/settings/WebsiteList";

const Account: React.FC = () => {
  const { userDetails } = useUserDetails();
  const [activeTab, setActiveTab] = useState<"account" | "settings">("account");
  const [autoSelectClientId, setAutoSelectClientId] = useState<string | undefined>(undefined);

  const handleClientCreated = (clientId: string) => {
    setAutoSelectClientId(clientId);
  };

  return (
    <section className="mx-auto text-center max-w-content">
      <AlphabetBackground fill="#e7dddc" opacity={0.5} />
      <UserAvatar size="w-20 h-20" showIndicator={false} />
      <h2
        className={`!font-medium text-center ml-4 text-primary mt-4 !mb-20 relative`}
      >
        {userDetails?.firstName} {userDetails?.lastName}
      </h2>
      <section className="relative flex flex-col items-start gap-4 p-6 mx-auto text-lg bg-base-100 rounded-3xl xl:p-12 w-full">
        <div role="tablist" className="tabs tabs-bordered w-full mb-6">
          <button
            role="tab"
            className={`tab ${activeTab === "account" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("account")}
          >
            Account
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === "settings" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("settings")}
          >
            Impostazioni
          </button>
        </div>

        {activeTab === "account" && (
          <div className="w-full">
            <div className="flex flex-col items-center justify-between gap-8 md:gap-32 md:flex-row">
              <h3
                className={`!mb-2 !text-4xl text-primary !font-normal flex items-center gap-3 ${
                  userDetails?.isPremium ? "!text-accent" : "!text-neutral"
                }`}
              >
                {userDetails?.isPremium ? "Premium" : "Free"} Plan
                {userDetails?.isPremium ? (
                  <CrownIcon className="w-8 h-8" />
                ) : (
                  ""
                )}
              </h3>
              {userDetails?.isPremium ? (
                <a
                  href={`${
                    process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL_URL
                  }?prefilled_email=${encodeURIComponent(
                    userDetails?.email || ""
                  )}`}
                  rel="noopener noreferrer"
                  className="no-underline link link-primary"
                >
                  Gestisci il mio abbonamento
                </a>
              ) : (
                <Link to="/checkout" className="no-underline link link-primary">
                  Fai l'upgrade del tuo piano
                </Link>
              )}
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="w-full space-y-8">
            <ClientList onClientCreated={handleClientCreated} />
            <div className="divider"></div>
            <WebsiteList autoSelectClientId={autoSelectClientId} />
          </div>
        )}
      </section>
    </section>
  );
};

export default Account;
